import React from 'react';
import ReactDOM from 'react-dom';
import parse from 'html-react-parser';
import loadable from '@loadable/component';
import domToReact from 'html-react-parser/lib/dom-to-react';
import mapKeys from 'lodash/mapKeys';
import { Link } from '../Components/Link/Link';
import { Time } from '~ui/Time';
import Currency from '~ui/Currency';
import BetslipKeyboard from '~ui/BetslipKeyboard';

const BetBuilder = loadable(
    () => import(/* webpackChunkName: "betbuilder" */ '~scenes/sport/brightbundles/BetBuilder')
);

/**
 * replaces a dom node with a react element.
 *
 * @param {node} domNode the dom node
 * @param {object} map the attribute to props map
 * @return {object} props for react element
 */
const getAttrs = (domNode, map = {}) => mapKeys(domNode.attribs, (val, key) => {
    return map[key] || key;
});

/**
 * replaces a dom node with a react element.
 *
 * @param {node} domNode the dom node
 * @return {react.element} the react element
 */
function replaceDomNodes(domNode) {
    switch (domNode.name) {
        case 'a': {
            const map = {
                href: 'to',
                class: 'className',
            };

            let gtmAttribute = null;
            const attrs = mapKeys(domNode.attribs, (val, key) => {
                gtmAttribute = (key === 'data-spectate-product') ? val : null;

                return map[key] || key;
            });

            return (
                <Link {...attrs} gtmAttribute={gtmAttribute} >
                    { domToReact(domNode.children, {
                        replace: replaceDomNodes,
                    }) }
                </Link>
            );
        }
        /**
         * Handles <time> DOM nodes used in legacy code. See client/src/shared/ui/Time/README.md
         * If format is provided, pass it down or <Time> just has a default value (DATE_TIME_FORMAT_HM)
         */
        case 'time': {
            const map = {
                datetime: 'dateTime',
                format: 'format',
            };
            const { dateTime, format } = mapKeys(domNode.attribs, (val, key) => {
                return map[key] || key;
            });
            const props = {
                dateTime,
                ...(format && { format }),
            };

            return (
                <Time {...props}>
                    { domToReact(domNode.children, {
                        replace: replaceDomNodes,
                    }) }
                </Time>
            );
        }
        case 'data': {
            const { value } = domNode.attribs;
            if (domNode.attribs.hasOwnProperty('data-currency')) {
                return (
                    <Currency value={value}>
                        { domToReact(domNode.children, {
                            replace: replaceDomNodes,
                        }) }
                    </Currency>);
            } else if (domNode.attribs.hasOwnProperty('data-betslip-keyboard')) {
                return <BetslipKeyboard isBabyBetslip={false} />;
            } else if (domNode.attribs.hasOwnProperty('data-brightbundles-iframe')) {
                return <BetBuilder {...getAttrs(domNode)}/>;
            }

            return <data value={value}>
                { domToReact(domNode.children, {
                    replace: replaceDomNodes,
                }) }
            </data>;
        }
        default:
    }

    return null;
}
/**
 * Converts HTML to React elements
 *
 * @param {String} html The HTML to convert to React elements
 * @return {React.Element} The React elements tree structure
 */
export function htmlToReact(html) {
    return parse(html, {
        replace: replaceDomNodes,
    });
}
/**
 * Renders the React element to a target dom element (using a portal).
 * You need to actually render the returned object into a react component
 * render method for it to display.
 *
 * @param {React.Element} elements The tree of React elements
 * @param {Node} target A DOM node
 * @returns {React.Portal} A react Portal
 */
export function renderToDom(elements, target) {
    return ReactDOM.createPortal(elements, target);
}
